import React, { useEffect, useState, useRef } from 'react';
import { Box, Typography, LinearProgress, Chip, Card, CardContent } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';

const XPProgressBar = ({ 
  userXP, 
  showDetails = true, 
  animate = true, 
  compact = false,
  onLevelUp,
  disableCelebrations = false
}) => {
  const [displayXP, setDisplayXP] = useState(disableCelebrations ? (userXP?.total_xp || 0) : 0);
  const [displayLevel, setDisplayLevel] = useState(disableCelebrations ? (userXP?.level_number || 1) : 1);
  const [showLevelUp, setShowLevelUp] = useState(false);
  const hasTriggeredLevelUp = useRef(false);

  useEffect(() => {
    if (!userXP) return;

    const targetXP = userXP.total_xp || 0;
    const targetLevel = userXP.level_number || 1;
    
    // Reset level up trigger flag when userXP changes
    hasTriggeredLevelUp.current = false;
    
    // Animate XP counting up
    if (animate) {
      const duration = 1500; // 1.5 seconds
      const steps = 60;
      const increment = (targetXP - displayXP) / steps;
      let currentStep = 0;

      const timer = setInterval(() => {
        currentStep++;
        setDisplayXP(prev => {
          const newXP = prev + increment;
          if (currentStep >= steps) {
            clearInterval(timer);
            return targetXP;
          }
          return newXP;
        });
      }, duration / steps);

      // Check for level up only once
      if (targetLevel > displayLevel && !hasTriggeredLevelUp.current && !disableCelebrations) {
        hasTriggeredLevelUp.current = true;
        setTimeout(() => {
          setDisplayLevel(targetLevel);
          setShowLevelUp(true);
          onLevelUp && onLevelUp(targetLevel);
          setTimeout(() => setShowLevelUp(false), 3000);
        }, duration * 0.8);
      } else if (targetLevel > displayLevel && disableCelebrations) {
        // Just update level without celebrations
        setDisplayLevel(targetLevel);
      }

      return () => {
        clearInterval(timer);
        hasTriggeredLevelUp.current = false;
      };
    } else {
      setDisplayXP(targetXP);
      setDisplayLevel(targetLevel);
      
      // Check for level up in non-animated mode
      if (targetLevel > displayLevel && !hasTriggeredLevelUp.current && !disableCelebrations) {
        hasTriggeredLevelUp.current = true;
        setShowLevelUp(true);
        onLevelUp && onLevelUp(targetLevel);
        setTimeout(() => setShowLevelUp(false), 3000);
      } else if (disableCelebrations) {
        // Just update level without celebrations
        setDisplayLevel(targetLevel);
      }
    }
    
    // Cleanup function
    return () => {
      hasTriggeredLevelUp.current = false;
    };
  }, [userXP, animate, onLevelUp, disableCelebrations]);

  if (!userXP) return null;

  // Calculate progress percentage
  const xpInCurrentLevel = userXP.xp_in_current_level || 0;
  const xpToNextLevel = userXP.xp_to_next_level || 0;
  const totalXPForLevel = xpInCurrentLevel + xpToNextLevel;
  
  const progressPercentage = totalXPForLevel > 0 
    ? (xpInCurrentLevel / totalXPForLevel) * 100
    : 100;

  const getLevelColor = (level) => {
    if (level >= 50) return '#9c27b0'; // Purple for high levels
    if (level >= 30) return '#ff9800'; // Orange for mid-high levels
    if (level >= 15) return '#2196f3'; // Blue for mid levels
    if (level >= 5) return '#4caf50';  // Green for early levels
    return '#607d8b'; // Grey for beginners
  };

  const getLevelTitle = (level) => {
    if (level >= 50) return 'Master';
    if (level >= 30) return 'Expert';
    if (level >= 15) return 'Advanced';
    if (level >= 5) return 'Intermediate';
    return 'Beginner';
  };

  const levelColor = getLevelColor(displayLevel);
  const levelTitle = getLevelTitle(displayLevel);

  if (!showDetails) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, minWidth: 200 }}>
        <Chip 
          label={`Level ${displayLevel}`}
          sx={{ 
            bgcolor: levelColor,
            color: 'white',
            fontWeight: 'bold',
            minWidth: 80
          }}
        />
        <Box sx={{ flex: 1 }}>
          <LinearProgress 
            variant="determinate" 
            value={progressPercentage}
            sx={{
              height: 8,
              borderRadius: 4,
              bgcolor: 'rgba(0,0,0,0.1)',
              '& .MuiLinearProgress-bar': {
                bgcolor: levelColor,
                borderRadius: 4
              }
            }}
          />
        </Box>
        <Typography variant="caption" color="text.secondary">
          {Math.round(displayXP)} XP
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative' }}>
      <Card 
        sx={{ 
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          border: `1px solid ${levelColor}20`,
          borderRadius: 3,
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
          position: 'relative',
          overflow: 'visible'
        }}
      >
        <CardContent sx={{ p: compact ? 2 : 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: compact ? 1.5 : 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: compact ? 1.5 : 2 }}>
              <Box
                sx={{
                  width: compact ? 36 : 50,
                  height: compact ? 36 : 50,
                  borderRadius: '50%',
                  background: `linear-gradient(135deg, ${levelColor}, ${levelColor}cc)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold',
                  fontSize: compact ? '0.9rem' : '1.2rem',
                  boxShadow: `0 4px 12px ${levelColor}40`
                }}
              >
                {displayLevel}
              </Box>
              <Box>
                <Typography variant={compact ? "body2" : "h6"} sx={{ fontWeight: 'bold', color: levelColor, fontSize: compact ? '0.85rem' : undefined }}>
                  Level {displayLevel}
                </Typography>
                <Typography variant={compact ? "caption" : "body2"} color="text.secondary" sx={{ fontSize: compact ? '0.7rem' : undefined }}>
                  {levelTitle}
                </Typography>
              </Box>
            </Box>
            
            <Box sx={{ textAlign: 'right' }}>
              <Typography variant={compact ? "body2" : "h6"} sx={{ fontWeight: 'bold', fontSize: compact ? '0.8rem' : undefined }}>
                {Math.round(displayXP).toLocaleString()} XP
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ fontSize: compact ? '0.65rem' : undefined }}>
                Total Experience
              </Typography>
            </Box>
          </Box>

          <Box sx={{ mb: compact ? 0.5 : 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: compact ? 0.5 : 1 }}>
              <Typography variant={compact ? "caption" : "body2"} sx={{ fontWeight: 'medium', fontSize: compact ? '0.7rem' : undefined }}>
                Progress to Level {displayLevel + 1}
              </Typography>
              <Typography variant={compact ? "caption" : "body2"} color="text.secondary" sx={{ fontSize: compact ? '0.65rem' : undefined }}>
                {xpInCurrentLevel} / {totalXPForLevel} XP
              </Typography>
            </Box>
            
            <Box
              sx={{
                width: '100%',
                height: compact ? 8 : 12,
                bgcolor: 'rgba(0,0,0,0.1)',
                borderRadius: compact ? 4 : 6,
                overflow: 'hidden',
                position: 'relative'
              }}
            >
              <motion.div
                initial={{ width: 0 }}
                animate={{ width: `${progressPercentage}%` }}
                transition={{ duration: animate ? 1.5 : 0, ease: 'easeOut' }}
                style={{
                  height: '100%',
                  background: `linear-gradient(90deg, ${levelColor}, ${levelColor}cc)`,
                  borderRadius: compact ? 4 : 6,
                  position: 'relative',
                  overflow: 'hidden'
                }}
              >
                {/* Shimmer effect */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)',
                    animation: 'shimmer 2s infinite',
                    '@keyframes shimmer': {
                      '0%': { transform: 'translateX(-100%)' },
                      '100%': { transform: 'translateX(100%)' }
                    }
                  }}
                />
              </motion.div>
            </Box>
          </Box>

          <Typography variant="caption" color="text.secondary" sx={{ fontSize: compact ? '0.6rem' : undefined }}>
            {xpToNextLevel} XP needed for next level
          </Typography>
        </CardContent>
      </Card>

      {/* Level Up Animation - Only show if celebrations are enabled */}
      <AnimatePresence>
        {showLevelUp && !disableCelebrations && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1600,
              pointerEvents: 'none'
            }}
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.5 }}
              transition={{ duration: 0.5, type: 'spring' }}
              style={{ pointerEvents: 'auto' }}
            >
              <Card
                sx={{
                  background: `linear-gradient(135deg, ${levelColor}, ${levelColor}cc)`,
                  color: 'white',
                  textAlign: 'center',
                  minWidth: 200,
                  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                  borderRadius: 3
                }}
              >
                <CardContent>
                  <Typography variant="h4" sx={{ mb: 1 }}>
                    🎉
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                    LEVEL UP!
                  </Typography>
                  <Typography variant="body1">
                    You reached Level {displayLevel}!
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                    {levelTitle}
                  </Typography>
                </CardContent>
              </Card>
            </motion.div>
          </Box>
        )}
      </AnimatePresence>
    </Box>
  );
};

export default XPProgressBar;
